import React, { useEffect, useRef, useState } from "react";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { Spinner, Table } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import { DatePicker } from "antd";
import { upperCase } from "lodash";
import { useSelector } from "react-redux";
import { DownloadTableExcel } from "react-export-table-to-excel";
import { baseUrl } from "../../../services/config";
const { RangePicker } = DatePicker;

const pageStyle = `
  @page {
    size: landscape !important;
}
`;

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatMonth = (date) => {
  if (!date) return "";
  const [year, month] = date.split("-");
  return `${monthNames[Number(month) - 1]}-${year}`;
};

const formatPercentage = (value) =>
  value === null || value === undefined ? "-" : `${value}%`;

const percentageColor = (value) => {
  if (value === null || value === undefined) return "inherit";
  if (value >= 90) return "#1bc5bd";
  if (value >= 75) return "#ffa800";
  return "#f64e60";
};

const AttendanceAnalysis = () => {
  const user = useSelector((state) => state.user.user);
  const alert = useAlert();
  let authtoken = localStorage.getItem("userToken");

  const [allSessions, setAllSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [campusData] = useState(user.campuses || []);
  const [campusId, setCampusId] = useState("");
  const [classesData, setClassesData] = useState([]);
  const [classId, setClassId] = useState("");
  const [educationType, setEducationType] = useState("1");
  const [sectionData, setSectionData] = useState([]);
  const [sectionId, setSectionId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [reportTitle, setReportTitle] = useState("");

  const [unitName, setUnitName] = useState("");
  const [icon, setIcon] = useState("");
  const [printLoading, setPrintLoading] = useState(false);
  const componentRef = useRef(null);
  // State rather than a ref so the Export button re-renders once the table exists.
  const [tableElement, setTableElement] = useState(null);

  useEffect(() => {
    getSettingData();
    getAllSessions();
  }, []);

  const getSettingData = () => {
    fetch(`${baseUrl}/get-settings`, {
      method: "GET",
      headers: { Authorization: `Bearer ${authtoken}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setUnitName(upperCase(data.payload.settings.unit_name));
        setIcon(data.payload.settings.logo_file);
      })
      .catch((err) => console.log(err));
  };

  const getAllSessions = () => {
    fetch(`${baseUrl}/session`, {
      method: "GET",
      headers: { Authorization: `Bearer ${authtoken}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setAllSessions(data.payload || []);
        (data.payload || []).forEach((session) => {
          if (session.active_academic_year == "1") {
            setSessionId(session.id);
          }
        });
      })
      .catch((err) => console.log(err));
  };

  const getSections = (campus, type, studentClass) => {
    setSectionId("");
    setSectionData([]);
    if (!campus || !studentClass) return;
    fetch(`${baseUrl}/class_section/${campus}/${type}/${studentClass}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${authtoken}` },
    })
      .then((res) => res.json())
      .then((data) => setSectionData(data.payload || []))
      .catch((err) => console.log(err));
  };

  const onCampusChange = (id) => {
    setCampusId(id);
    setClassId("");
    setClassesData([]);
    setSectionId("");
    setSectionData([]);
    if (!id) return;
    fetch(`${baseUrl}/campusclass/${id}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${authtoken}` },
    })
      .then((res) => res.json())
      .then((data) => setClassesData(data.payload || []))
      .catch((err) => console.log(err));
  };

  const onClassChange = (id) => {
    setClassId(id);
    getSections(campusId, educationType, id);
  };

  const onEducationTypeChange = (type) => {
    setEducationType(type);
    getSections(campusId, type, classId);
  };

  const onMonthChange = (_, dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setStartDate("");
      setEndDate("");
      return;
    }
    const [endYear, endMonth] = dates[1].split("-").map(Number);
    const lastDay = new Date(endYear, endMonth, 0).getDate();
    setStartDate(`${dates[0]}-01`);
    setEndDate(`${dates[1]}-${String(lastDay).padStart(2, "0")}`);
  };

  // Validation errors arrive as { field: [messages] }; anything else as a string.
  const getErrorMessage = (data) => {
    const message = data?.metadata?.message ?? data?.message;
    if (message && typeof message === "object") {
      const first = Object.values(message)[0];
      return Array.isArray(first) ? first[0] : String(first);
    }
    return message || "Something went wrong, please try again";
  };

  const handleSearch = () => {
    if (!sessionId) return alert.error("Session is required");
    if (!startDate || !endDate) return alert.error("Month is required");
    if (!campusId) return alert.error("Campus is required");

    const campusName = campusData.find((c) => c.id == campusId)?.name || "";
    const className = classesData.find((c) => c.id == classId)?.name;
    const sectionName = sectionData.find((s) => s.id == sectionId)?.name;
    setReportTitle(
      `Attendance Analysis of ${campusName}${
        className ? ` ${className}` : ""
      }${sectionName ? ` (${sectionName})` : ""} From ${formatMonth(
        startDate.slice(0, 7)
      )} To ${formatMonth(endDate.slice(0, 7))}`
    );

    setLoading(true);
    fetch(`${baseUrl}/attendance-analysis`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${authtoken}`,
      },
      body: JSON.stringify({
        year_id: sessionId,
        campus_id: campusId,
        student_class_id: classId || null,
        global_section_id: sectionId || null,
        education_type: educationType,
        start_date: startDate,
        end_date: endDate,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setLoading(false);
        setSearched(true);
        if (data?.metadata?.success) {
          setRows(data.payload.rows || []);
          setTotals(data.payload.totals || null);
          if (!data.payload.rows || data.payload.rows.length === 0) {
            alert.error("No Record Found");
          }
        } else {
          setRows([]);
          setTotals(null);
          alert.error(getErrorMessage(data));
        }
      })
      .catch((err) => {
        setLoading(false);
        setSearched(true);
        setRows([]);
        setTotals(null);
        alert.error("Unable to load attendance analysis, please try again");
        console.log(err);
      });
  };

  const cell = { textAlign: "center", verticalAlign: "middle" };

  const renderRow = (row, key, label, isTotal = false) => (
    <tr
      key={key}
      style={isTotal ? { fontWeight: "bold", borderTop: "2px solid black" } : {}}
    >
      <td style={cell}>{isTotal ? "" : key + 1}</td>
      <td style={{ ...cell, textAlign: "start" }} colSpan={isTotal ? 2 : 1}>
        {label ?? row.class}
      </td>
      {!isTotal && <td style={{ ...cell, textAlign: "start" }}>{row.section}</td>}
      <td style={cell}>{row.students}</td>
      <td style={cell}>{row.absent}</td>
      <td style={cell}>{row.sick}</td>
      <td style={cell}>{row.leave}</td>
      <td
        style={{
          ...cell,
          fontWeight: "bold",
          color: percentageColor(row.attendance_percentage),
        }}
      >
        {formatPercentage(row.attendance_percentage)}
      </td>
      <td style={cell}>{row.days_marked}</td>
      <td style={cell}>{row.late_coming}</td>
      <td style={cell}>{row.home_work_not_done}</td>
      <td style={cell}>{row.improper_uniform}</td>
      <td style={cell}>{row.test_not_prepared}</td>
    </tr>
  );

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      <div className="row">
        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Session</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
          >
            <option value="">Select Session</option>
            {allSessions.map((session) => (
              <option key={session.id} value={session.id}>
                {session.year}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Month</b>
          </label>
          <RangePicker
            picker="month"
            className="h-40px w-100"
            onChange={onMonthChange}
          />
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Campus</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={campusId}
            onChange={(e) => onCampusChange(e.target.value)}
          >
            <option value="">Select Campus</option>
            {campusData.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Class (Optional)</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={classId}
            onChange={(e) => onClassChange(e.target.value)}
            disabled={!campusId}
          >
            <option value="">All Classes</option>
            {classesData.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Education Type</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={educationType}
            onChange={(e) => onEducationTypeChange(e.target.value)}
          >
            <option value="1">Education</option>
            <option value="2">Hifz</option>
          </select>
        </div>

        <div className="col-md-3 pb-4">
          <label className="form-control-label">
            <b>Select Section (Optional)</b>
          </label>
          <select
            className="form-control form-select h-40px"
            value={sectionId}
            onChange={(e) => setSectionId(e.target.value)}
            disabled={!classId}
          >
            <option value="">All Sections</option>
            {sectionData.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6 mt-7">
          <button
            className="btn btn-primary"
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </button>

          {rows.length > 0 ? (
            <>
              <ReactToPrint
                documentTitle="Attendance Analysis Report"
                pageStyle={pageStyle}
                removeAfterPrint={true}
                onBeforeGetContent={() => setPrintLoading(true)}
                onAfterPrint={() => setPrintLoading(false)}
                trigger={() => (
                  <button
                    disabled={printLoading}
                    className="btn btn-primary w-105px mx-2"
                  >
                    {printLoading ? <span>Printing..</span> : "Print"}
                  </button>
                )}
                content={() => componentRef.current}
              />
              <DownloadTableExcel
                filename="Attendance Analysis Report"
                sheet="Attendance Analysis"
                currentTableRef={tableElement}
              >
                <button className="btn btn-primary mx-2">Export</button>
              </DownloadTableExcel>
            </>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center mt-5">
          <Spinner variant="info" animation="border" />
        </div>
      ) : rows.length > 0 ? (
        <div className="mt-5" ref={componentRef}>
          <div className="row p-3">
            <img style={{ width: 100, height: 100 }} src={icon} alt="logo" />
            <div className="col-md-10" style={{ lineHeight: 1.5 }}>
              <h1>{unitName}</h1>
              <h4>{reportTitle}</h4>
            </div>
          </div>

          <Table responsive bordered ref={setTableElement}>
            <thead>
              <tr style={cell}>
                <th style={cell}>Sr</th>
                <th style={cell}>Class</th>
                <th style={cell}>Section</th>
                <th style={cell}>Students</th>
                <th style={cell}>Absent</th>
                <th style={cell}>Sick</th>
                <th style={cell}>Leave</th>
                <th style={cell}>Attendance %</th>
                <th style={cell}>Days Marked</th>
                <th style={cell}>Late Coming</th>
                <th style={cell}>Home Work Not Done</th>
                <th style={cell}>Improper Uniform</th>
                <th style={cell}>Test Not Prepared</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => renderRow(row, index))}
              {totals && renderRow(totals, "total", "Total", true)}
            </tbody>
          </Table>

          <p className="text-muted mt-3" style={{ fontSize: "12px" }}>
            Attendance % = present student-days ÷ (students × working days),
            where working days are Monday to Saturday up to today and Absent,
            Sick and Leave count as not present. Days Marked = days on which at
            least one student of the section was marked.
          </p>
        </div>
      ) : searched ? (
        <div className="text-center p-5">
          <h4 className="text-muted">No record found for the selected criteria</h4>
        </div>
      ) : null}
    </Paper>
  );
};

export default AttendanceAnalysis;
